'use client';

import { useRouter } from 'next/navigation';
import { useState } from 'react';

interface HireButtonProps {
  equipment: {
    id: string;
    name: string;
    slug: string;
    primaryImage?: string;
    dailyPrice?: number;
    weeklyPrice?: number;
  };
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  children?: React.ReactNode;
}

export function HireButton({ 
  equipment, 
  variant = 'primary', 
  size = 'md',
  className = '',
  children 
}: HireButtonProps) {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const handleHireClick = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    
    try {
      // Navigate to SEO-friendly hire page with equipment slug
      router.push(`/hire/${equipment.slug}`);
    } catch (error) {
      console.error('Error navigating to hire page:', error);
      setIsLoading(false);
    }
  };

  // Variant styles
  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        return 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 border-transparent';
      case 'secondary':
        return 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 border-transparent';
      case 'outline':
        return 'bg-white text-blue-600 hover:bg-blue-50 focus:ring-blue-500 border-blue-300 hover:border-blue-400';
      default:
        return 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 border-transparent';
    }
  };

  // Size styles
  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return 'px-3 py-1.5 text-sm';
      case 'md':
        return 'px-4 py-2 text-sm';
      case 'lg':
        return 'px-6 py-3 text-base';
      default:
        return 'px-4 py-2 text-sm';
    }
  };

  return (
    <button
      onClick={handleHireClick}
      disabled={isLoading}
      className={`
        inline-flex items-center justify-center
        font-medium rounded-md border
        transition-colors duration-200
        focus:outline-none focus:ring-2 focus:ring-offset-2
        disabled:opacity-50 disabled:cursor-not-allowed
        ${getVariantStyles()}
        ${getSizeStyles()}
        ${className}
      `}
    >
      {isLoading ? (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
          Loading...
        </>
      ) : (
        <>
          {children || (
            <>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Hire Now
            </>
          )}
        </>
      )}
    </button>
  );
}

// Quick hire button with pricing display
export function QuickHireButton({ equipment }: { equipment: HireButtonProps['equipment'] }) {
  // Format currency helper
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
    }).format(amount);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="font-medium text-gray-900 text-sm">{equipment.name}</h4>
          <div className="text-xs text-gray-600 mt-1">
            {equipment.dailyPrice && equipment.dailyPrice > 0 && (
              <span className="block">{formatCurrency(equipment.dailyPrice)}/day</span>
            )}
            {equipment.weeklyPrice && equipment.weeklyPrice > 0 && (
              <span className="block text-green-600">{formatCurrency(equipment.weeklyPrice)}/week</span>
            )}
          </div>
        </div>
        {equipment.primaryImage && (
          <img
            src={equipment.primaryImage}
            alt={equipment.name}
            className="w-12 h-12 object-cover rounded"
          />
        )}
      </div>
      
      <HireButton 
        equipment={equipment}
        variant="primary"
        size="sm"
        className="w-full"
      >
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a1 1 0 011-1h6a1 1 0 011 1v4h3a1 1 0 011 1v8a1 1 0 01-1 1H5a1 1 0 01-1-1V8a1 1 0 011-1h3z" />
        </svg>
        Quick Hire
      </HireButton>
    </div>
  );
}

// Floating action button for mobile
export function FloatingHireButton({ equipment }: { equipment: HireButtonProps['equipment'] }) {
  return (
    <div className="fixed bottom-4 right-4 z-50 md:hidden">
      <HireButton 
        equipment={equipment}
        variant="secondary"
        size="lg"
        className="rounded-full shadow-lg"
      >
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        Hire
      </HireButton>
    </div>
  );
}